"use client";

import { WebsiteLegalFooter } from "@/app/(public)/_components/website-legal-footer";
import { MarketingPageView } from "@/lib/marketing-analytics/marketing-page-view";
import { useViewportBelow } from "@/lib/use-mobile-viewport";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import "./pricing.css";
import { usePricingCalculator } from "./pricing-calculator";
import { PricingDesktop } from "./pricing-desktop";
import { PricingMobile } from "./pricing-mobile";
import type { InfoId } from "./pricing-popovers";

// Desktop grids need ~1024px; tablets below get the single-column layout.
const PRICING_DESKTOP_MIN_WIDTH_PX = 1024;

/** Marketing pricing page: hero, included catalog, cost disclosure, savings calculator. */
export function PricingPage(): ReactNode {
  const isMobile = useViewportBelow(PRICING_DESKTOP_MIN_WIDTH_PX);
  const calc = usePricingCalculator();
  const [openInfo, setOpenInfo] = useState<InfoId | null>(null);

  const closeInfo = useCallback(() => setOpenInfo(null), []);
  const toggleInfo = useCallback((id: InfoId) => {
    setOpenInfo((prev) => (prev === id ? null : id));
  }, []);

  // Dismiss the open popover on outside click (unless inside a kept element) or Escape.
  useEffect(() => {
    if (!openInfo) return;
    const onDown = (e: MouseEvent): void => {
      const target = e.target;
      if (target instanceof Element && target.closest("[data-info-keep]"))
        return;
      setOpenInfo(null);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpenInfo(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openInfo]);

  const Layout = isMobile ? PricingMobile : PricingDesktop;

  return (
    <div className="mkpreise">
      <MarketingPageView funnel="website_demo" step={0} />
      <Layout
        calc={calc}
        openInfo={openInfo}
        onToggle={toggleInfo}
        onClose={closeInfo}
      />
      <WebsiteLegalFooter background="#FFFFFF" />
    </div>
  );
}

export default PricingPage;
